import { Country, Listing } from '@smg-real-estate/hg-rets-schema';

import { deleteRequest, get, patch, post, put } from '@/api/fetch';
import { DeactivationReason } from '@/components/InsertionCard/ListingActionModals/modals/DeactivationModal/deactivationReason';
import {
  CustomerSegment,
  FiltersWithCount,
  InsertionStatus,
  SourcePlatform,
  type Insertion,
} from '@/types';

import { getPlatformHeader } from '@/helpers/apiUtils';

export interface PaginatedApiResponseBody {
  data: Insertion[];
  cursors?: {
    next?: string;
    totalCount?: number;
    pageSize?: number;
    totalPages?: number;
  };
  filtersWithCount: FiltersWithCount;
}

export interface SearchResult {
  insertions: Insertion[];
  nextStatus?: InsertionStatus;
  nextPageCursor?: string;
}

export interface GeoLookupItem {
  livingSpace?: number;
  numberOfRooms?: number;
  yearBuilt?: number;
  floor?: number;
  hasBalcony?: boolean;
  hasElevator?: boolean;
  hasSwimmingPool?: boolean;
  isMinergieCertified?: boolean;
  numberOfBathrooms?: number;
}

export interface GeoLookupParams {
  countryCode: Country;
  postCode: string;
  locality: string;
  street: string;
}

export function getApiEndpointUrl(userId: string, insertionId = ''): string {
  const insertionUrl = `${import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL}/users/${userId}/insertions`;
  if (insertionId) {
    return `${insertionUrl}/${insertionId}`;
  }
  return insertionUrl;
}

export function getApiUnifiedInsertionEndpointUrl(userId: string, insertionId = ''): string {
  const insertionUrl = `${import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL}/users/${userId}/unified-insertions`;
  if (insertionId) {
    return `${insertionUrl}/${insertionId}`;
  }
  return insertionUrl;
}

export async function searchInsertions(
  searchParameters: Record<string, unknown>,
  sourcePlatform: SourcePlatform,
): Promise<PaginatedApiResponseBody> {
  let endpoint = `${import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL}/insertions/search`;
  endpoint = endpoint.concat(getQueryStringParameters(searchParameters));

  const response: Response = await get(endpoint, undefined, getPlatformHeader(sourcePlatform));
  return response.json();
}

export async function geoLookup({
  countryCode,
  postCode,
  locality,
  street,
}: GeoLookupParams): Promise<GeoLookupItem[]> {
  const queryStringParams = new URLSearchParams({
    countryCode,
    postCode,
    locality,
    street,
  }).toString();
  const { VUE_APP_INSERTION_FUNNEL_API_BASE_URL } = import.meta.env;

  const response = await get(
    `${VUE_APP_INSERTION_FUNNEL_API_BASE_URL}/geo-lookup?${queryStringParams}`,
    undefined,
    getPlatformHeader(SourcePlatform.SMG),
  );
  return response.json();
}

const getQueryStringParameters = (searchParameters: any) => {
  Object.keys(searchParameters).forEach((key) => {
    if (!searchParameters[key] || searchParameters[key]?.length === 0) delete searchParameters[key];
  });

  const urlSearchParams = new URLSearchParams(searchParameters).toString();
  return urlSearchParams === '' ? urlSearchParams : `?${urlSearchParams}`;
};

export async function getUnifiedInsertionById(
  userId: string,
  insertionId: string,
  sourcePlatform: SourcePlatform,
): Promise<Insertion> {
  let url = getApiUnifiedInsertionEndpointUrl(userId, insertionId);

  if (import.meta.env.DEV) {
    const mockInsertion = new URLSearchParams(window.location.search).get('mockInsertion');
    if (mockInsertion) {
      url = `${url}?mockInsertion=${encodeURIComponent(mockInsertion)}`;
    }
  }

  const response: Response = await get(url, undefined, getPlatformHeader(sourcePlatform));
  return response.json();
}

export async function postInsertion(
  userId: string,
  listing: Listing,
  sourcePlatform: SourcePlatform,
): Promise<Insertion> {
  const response: Response = await post(
    getApiEndpointUrl(userId),
    JSON.stringify(listing),
    getPlatformHeader(sourcePlatform),
  );
  return response.json();
}

export async function putInsertion(
  userId: string,
  insertionId: string,
  listing: Listing,
  sourcePlatform: SourcePlatform,
): Promise<void> {
  await put(
    getApiEndpointUrl(userId, insertionId),
    JSON.stringify(listing),
    getPlatformHeader(sourcePlatform),
  );
}

export async function deleteInsertion(
  ownerId: string,
  insertionId: string,
  sourcePlatform: SourcePlatform,
): Promise<void> {
  await deleteRequest(
    getApiEndpointUrl(ownerId, insertionId),
    undefined,
    getPlatformHeader(sourcePlatform),
  );
}

export async function patchInsertionStatus(
  userId: string,
  insertionId: string,
  insertionStatus: string,
  sourcePlatform: SourcePlatform,
): Promise<void> {
  const body = JSON.stringify({ status: insertionStatus });

  const endpoint = getApiEndpointUrl(userId);
  await patch(`${endpoint}/${insertionId}`, body, getPlatformHeader(sourcePlatform));
}

export async function postInsertionCustomerSegment(
  userId: string,
  insertionId: string,
  sourcePlatform: SourcePlatform,
): Promise<void> {
  const body = JSON.stringify({ customerSegment: CustomerSegment.PROFESSIONAL });
  await post(
    `${getApiEndpointUrl(userId, insertionId)}/customer-segment`,
    body,
    getPlatformHeader(sourcePlatform),
  );
}

export async function putInsertionFurthestPage(
  userId: string,
  insertionId: string,
  furthestPage: string,
  sourcePlatform: SourcePlatform,
): Promise<void> {
  const body = JSON.stringify({ furthestPage });
  await put(
    `${getApiEndpointUrl(userId, insertionId)}/furthestpage`,
    body,
    getPlatformHeader(sourcePlatform),
  );
}

export async function postInsertionSubmittal(
  userId: string,
  insertionId: string,
  sourcePlatform: SourcePlatform,
): Promise<Insertion> {
  const response: Response = await post(
    `${getApiEndpointUrl(userId, insertionId)}/submittals`,
    JSON.stringify({}),
    getPlatformHeader(sourcePlatform),
  );

  return response.json();
}

export async function patchSeonSession(
  userId: string,
  insertionId: string,
  sourcePlatform: SourcePlatform,
  seonSession: string,
  mfaData: {
    userEnrolledInMFA: boolean;
    requiredMFA?: boolean;
    mfaEnrollmentDateUtc?: string;
  },
): Promise<any> {
  const response: Response = await patch(
    `${getApiEndpointUrl(userId, insertionId)}/seon-session`,
    JSON.stringify({
      seonSession,
      ...mfaData,
    }),
    getPlatformHeader(sourcePlatform),
  );

  return response.json();
}

export async function postInsertionReactivate(
  userId: string,
  insertionId: string,
  sourcePlatform: SourcePlatform,
): Promise<Insertion> {
  const endpoint = `${import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL}/business/${userId}/insertions/${insertionId}/reactivate`;
  const response: Response = await put(endpoint, undefined, getPlatformHeader(sourcePlatform));
  return response.json();
}

export async function putPublishAddress(
  userId: string,
  insertionId: string,
  sourcePlatform: SourcePlatform,
  shouldHideAddress: boolean,
): Promise<Insertion> {
  const endpoint = `${import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL}/business/${userId}/insertions/${insertionId}/publish-address`;
  const response: Response = await put(
    endpoint,
    JSON.stringify({ shouldHideAddress }),
    getPlatformHeader(sourcePlatform),
  );
  return response.json();
}

export async function postInsertionDuplication(
  userId: string,
  insertionId: string,
  sourcePlatform: SourcePlatform,
): Promise<Insertion> {
  const response: Response = await post(
    `${getApiEndpointUrl(userId, insertionId)}/duplicate`,
    undefined,
    getPlatformHeader(sourcePlatform),
  );
  return response.json();
}

export function postInsertionArchive(
  userId: string,
  insertionId: string,
  archivalReasonKey: DeactivationReason,
  archivalReasonText: string,
  sourcePlatform: SourcePlatform,
): Promise<Response> {
  return post(
    `${getApiEndpointUrl(userId, insertionId)}/archive`,
    JSON.stringify({
      archivalReason: {
        key: archivalReasonKey,
        reason: archivalReasonText,
      },
    }),
    getPlatformHeader(sourcePlatform),
  );
}
